import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { closeBatchMode, closeDetails, selectGroupDetails } from "@dashboard/devices/store/index";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SidePanel } from "@dashboard/shared/components/SidePanel/SidePanel";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { SecondaryButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header, TableHeaderRows } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { Tooltip } from "@shared/Tooltip";
import {
    ColumnFiltersState,
    ExpandedState,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { GroupFilterHeader } from "../shared/components/GroupFilterHeader/GroupFilterHeader";
import { getBadgeContent } from "../shared/Filtering.utils";

import CreateGroupDialog from "./components/CreateGroupDialog";
import { GroupTableRow } from "./components/GroupTableRow";
import { selectGroups } from "./store/selectors";
import {
    buildGroupTableDataForSelectionFilter,
    customGlobalFilterFn,
    findRootRowIndexForGroup,
    getColumns,
    getExpandedStateForGroup,
    getGroupIdsFromNameColumnFilter,
    getGroupsTableData,
    GroupOverviewData,
} from "./GroupPage.utils";
import { useStyles } from "./GroupsPage.style";

const GroupsPage = () => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(UserSettingsService.getGroupPageColumnFilters());
    const [expanded, setExpanded] = React.useState<ExpandedState>(UserSettingsService.getGroupPageExpanded());
    const [globalFilter, setGlobalFilter] = React.useState(UserSettingsService.getGroupPageSearch());
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

    const { t } = useTranslation(["pages", "common"]);
    const { classes } = useStyles();
    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const dispatch = useDispatch();
    const { groupAbility } = PermissionService();

    const groups = useSelector(selectGroups);
    const { data: workspace } = useCurrentWorkspace();

    const isExpired = useCurrentSubscriptionExpired();
    const details = useSelector(selectGroupDetails);

    const selectedNameFilterIds = React.useMemo(() => getGroupIdsFromNameColumnFilter(columnFilters), [columnFilters]);

    const tableData = React.useMemo(() => {
        const base = getGroupsTableData(groups);
        if (selectedNameFilterIds.length === 0) {
            return base;
        }
        return buildGroupTableDataForSelectionFilter(base, groups, selectedNameFilterIds);
    }, [groups, selectedNameFilterIds]);

    const state = React.useMemo(() => ({ globalFilter, expanded, columnFilters }), [expanded, globalFilter, columnFilters]);

    const columns = React.useMemo(() => getColumns(globalFilter), [globalFilter]);

    const table = useReactTable<GroupOverviewData>({
        data: tableData,
        columns,
        state,
        globalFilterFn: customGlobalFilterFn,
        getColumnCanGlobalFilter: () => true,
        initialState: {
            pagination: {
                pageSize: UserSettingsService.getGroupPageRowsPerPage(),
                pageIndex: 0,
            },
        },
        getRowId: (row) => String(row.id),
        getSubRows: (row) => row.children,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onExpandedChange: setExpanded,
        getExpandedRowModel: getExpandedRowModel(),
        // Pages hold whole subtrees: the page size counts root groups, and an expanded group's
        // children ride along with it instead of being pushed onto the next page.
        paginateExpandedRows: false,
        getPaginationRowModel: getPaginationRowModel(),
    });

    const { pageIndex, pageSize } = table.getState().pagination;
    const rootGroupCount = table.getFilteredRowModel().rows.length;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [
        pageIndex,
        pageSize,
        tableData.length,
        expanded,
    ]);

    const setFilters = (id: "name", values: Record<string, boolean>) => {
        setColumnFilters((prev) => {
            const prevFiltersWithoutId = prev.filter((filter) => filter.id !== id);
            const value = Object.keys(_.pickBy(values, (item) => item));
            return [{ id, value }, ...prevFiltersWithoutId];
        });
    };
    const getTooltipTitle = () => {
        if (isExpired) {
            return t("pages:groupPage.tooltip.subscriptionExpired");
        }
        if (groupAbility(null, workspace).cannot("create", "Group")) {
            return t("pages:groupPage.tooltip.cannotCreateGroup");
        }
        return "";
    };

    const handleResetFilter = () => {
        setColumnFilters([]);
        setGlobalFilter("");
        table.setPageIndex(0);
    };

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("groupPage.columnFilters", columnFilters);
    }, [columnFilters]);

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("groupPage.expanded", expanded);
    }, [expanded]);

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("groupPage.search", globalFilter);
    }, [globalFilter]);

    React.useEffect(() => {
        table.setPageIndex(0);
    }, [globalFilter, columnFilters, table]);

    React.useEffect(() => {
        if (details?.selectedId && tableData.length > 0) {
            setExpanded((prev) => getExpandedStateForGroup(details.selectedId, groups, prev));

            const rootRowIndex = findRootRowIndexForGroup(table.getFilteredRowModel().rows, details.selectedId);
            if (rootRowIndex >= 0) {
                table.setPageIndex(Math.floor(rootRowIndex / pageSize));
            }
        }
    }, [details?.selectedId, tableData, groups, table, pageSize]);

    React.useEffect(() => {
        return () => {
            dispatch(closeDetails());
            dispatch(closeBatchMode());
        };
    }, [dispatch]);

    return (
        <PageContainer fitViewport>
            <ExpiredWrapper>
                <Box className={layoutClasses.tableLayout}>
                    <div className={classes.searchContainer}>
                        <QueryBuilderField
                            name="group-search"
                            autoComplete="group-search-field"
                            placeholder={t("common:button.search")}
                            value={globalFilter}
                            onChange={setGlobalFilter}
                            onClear={() => setGlobalFilter("")}
                        />
                        <Tooltip title={getTooltipTitle()} hide={groupAbility(null, workspace).can("create", "Group")}>
                            <SecondaryButton
                                onClick={() => setCreateDialogOpen(true)}
                                disabled={groupAbility(null, workspace).cannot("create", "Group")}
                                startIcon={<i className="fa-solid fa-plus" />}
                                size="small"
                                name="add-group"
                            >
                                {t("pages:groupPage.button.addGroup")}
                            </SecondaryButton>
                        </Tooltip>
                    </div>
                    <Box className={layoutClasses.tableWrapper}>
                        <MinimalTableContainer
                            ref={tableContainerRef}
                            className={cx(
                                layoutClasses.tableContainer,
                                !contentOverflows && layoutClasses.tableContainerNoScroll,
                            )}
                        >
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <TableHeaderRows
                                        headerGroups={table.getHeaderGroups()}
                                        renderHeader={(header) => {
                                            if (header.id === "name") {
                                                return (
                                                    <GroupFilterHeader
                                                        header={header}
                                                        table={table}
                                                        badgeContent={getBadgeContent("name", columnFilters)}
                                                        onSubmit={(values) => setFilters("name", values)}
                                                    />
                                                );
                                            }
                                            return <Header {...header} />;
                                        }}
                                    />
                                </MinimalTableHeader>
                                <TableBody<GroupOverviewData>
                                    table={table}
                                    filter={{
                                        isActive: !_.isEmpty(columnFilters) || !_.isEmpty(globalFilter),
                                        onResetFilter: handleResetFilter,
                                    }}
                                    noDataMessage={t("pages:groupPage.table.noGroups")}
                                >
                                    {table.getRowModel().rows.map((row) => (
                                        <GroupTableRow key={row.id} {...row} />
                                    ))}
                                </TableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                    </Box>
                    <CustomTablePagination
                        component="div"
                        className={layoutClasses.pagination}
                        rowsPerPageOptions={[12, 24, 48]}
                        count={rootGroupCount}
                        rowsPerPage={pageSize}
                        page={pageIndex}
                        onPageChange={(_event, page) => table.setPageIndex(page)}
                        onRowsPerPageChange={(event) => {
                            const size = Number(event.target.value);
                            table.setPageSize(size);
                            table.setPageIndex(0);
                            UserSettingsService.setWorkspaceValue("groupPage.rowsPerPage", size);
                        }}
                        slotProps={{ select: { name: "rows-per-page" } }}
                    />
                    <CreateGroupDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />
                </Box>
            </ExpiredWrapper>
            <SidePanel />
        </PageContainer>
    );
};

export default withMetaDescription(GroupsPage, {
    title: i18next.t("pages:groupPage.metaTitle"),
});
